import type { AccountStatus } from '@/api/types';

/**
 * Two tiny external stores the live stream writes and React reads through
 * `useSyncExternalStore`.
 *
 * They are plain modules rather than context so the stream handler — which is
 * not a component — can write to them, and so a test can drive them with no
 * renderer. `useSyncExternalStore` rather than `useState` in an effect is the
 * pattern this codebase already owes `OfflineIndicator` and `useMediaQuery`
 * (see the standing lint warnings in `eslint.config.js`); a new subscription to
 * a non-React source is written the right way from the start.
 */

/* ------------------------------------------------------- pending arrivals */

/**
 * Mail that has arrived but has deliberately **not** been put into the list.
 *
 * Splicing a row into a virtualized list the user is scrolled into moves every
 * row under their cursor and their pointer, so an arrival is parked here and
 * surfaced as the "N new messages" pill (doc 06 §5). Clicking the pill is what
 * refetches; nothing moves until the user asks.
 */
type ArrivalKey = string;

/** Not a character an opaque id or a folder id can contain. */
const KEY_SEPARATOR = ' ';

const arrivals = new Map<ArrivalKey, Set<string>>();
const arrivalListeners = new Set<() => void>();
/** Bumped on every write so `getSnapshot` can return a stable primitive. */
let arrivalVersion = 0;

function arrivalKey(accountId: string, folderId: string): ArrivalKey {
  return `${accountId}${KEY_SEPARATOR}${folderId}`;
}

function notifyArrivals(): void {
  arrivalVersion += 1;
  for (const listener of [...arrivalListeners]) listener();
}

export function subscribeArrivals(listener: () => void): () => void {
  arrivalListeners.add(listener);
  return () => {
    arrivalListeners.delete(listener);
  };
}

/** A version counter, so `useSyncExternalStore` compares a number, not a Set. */
export function arrivalsVersion(): number {
  return arrivalVersion;
}

export function recordArrivals(
  accountId: string,
  folderId: string,
  messageIds: readonly string[],
): void {
  if (messageIds.length === 0) return;
  const key = arrivalKey(accountId, folderId);
  const set = arrivals.get(key) ?? new Set<string>();
  const before = set.size;
  for (const id of messageIds) set.add(id);
  if (set.size === before && arrivals.has(key)) return;
  arrivals.set(key, set);
  notifyArrivals();
}

export function arrivalCount(accountId: string | undefined, folderId: string | undefined): number {
  if (accountId === undefined || folderId === undefined) return 0;
  return arrivals.get(arrivalKey(accountId, folderId))?.size ?? 0;
}

/** The ids parked for one folder — to tell whether a fetched list already holds them. */
export function arrivalIds(accountId: string, folderId: string): ReadonlySet<string> {
  return arrivals.get(arrivalKey(accountId, folderId)) ?? new Set();
}

/** Called when the list is refetched, and when a folder's ids stop meaning anything. */
export function clearArrivals(accountId: string, folderId?: string): void {
  let changed = false;
  for (const key of [...arrivals.keys()]) {
    const [account, folder] = key.split(KEY_SEPARATOR);
    if (account !== accountId) continue;
    if (folderId !== undefined && folder !== folderId) continue;
    arrivals.delete(key);
    changed = true;
  }
  if (changed) notifyArrivals();
}

/** Test seam: forget everything between cases. */
export function resetArrivals(): void {
  arrivals.clear();
  notifyArrivals();
}

/* --------------------------------------------------------- account status */

/**
 * The per-account connection state the stream reports (`account_status`).
 *
 * `reauth-required` is **terminal**: the backend has stopped retrying and will
 * not start again until it has a password, so the indicator does not show a
 * spinner and the app raises the re-auth prompt. Presenting it as
 * "reconnecting" would be a lie that never resolves.
 */
export interface AccountStreamStatus {
  status: AccountStatus;
  reason?: string;
  at: number;
}

const statuses = new Map<string, AccountStreamStatus>();
const statusListeners = new Set<() => void>();
let statusVersion = 0;

/** The stream spells statuses with underscores; `types.ts` uses a hyphen. */
function normalizeStatus(raw: string): AccountStatus | undefined {
  switch (raw.trim().toLowerCase()) {
    case 'connected':
      return 'connected';
    case 'connecting':
      return 'connecting';
    case 'degraded':
      return 'degraded';
    case 'error':
      return 'error';
    case 'reauth_required':
    case 'reauth-required':
      return 'reauth-required';
    default:
      return undefined;
  }
}

export function subscribeAccountStatus(listener: () => void): () => void {
  statusListeners.add(listener);
  return () => {
    statusListeners.delete(listener);
  };
}

export function accountStatusVersion(): number {
  return statusVersion;
}

export function recordAccountStatus(
  accountId: string,
  raw: string,
  reason?: string,
): AccountStatus | undefined {
  const status = normalizeStatus(raw);
  if (status === undefined) return undefined;
  statuses.set(accountId, { status, ...(reason ? { reason } : {}), at: Date.now() });
  statusVersion += 1;
  for (const listener of [...statusListeners]) listener();
  return status;
}

export function accountStreamStatus(
  accountId: string | undefined,
): AccountStreamStatus | undefined {
  if (accountId === undefined) return undefined;
  return statuses.get(accountId);
}

/** Test seam. */
export function resetAccountStatus(): void {
  statuses.clear();
  statusVersion += 1;
  for (const listener of [...statusListeners]) listener();
}

/* ------------------------------------------------------------ stream gaps */

/**
 * "The live stream stopped delivering for a while, and nothing can say what was
 * missed."
 *
 * Deliberately a different thing from an arrival. An arrival *names* the
 * messages it is holding back; a gap names nothing, because the connection was
 * down. The server's replay ring only reaches back ten minutes, and — the part
 * that makes this the stale-list bug — a reopen that carries **no** resume
 * point is treated by the server as a brand-new stream and gets no
 * `resync_required` at all. A tab that connected, sat through a quiet mailbox
 * and lost its socket has no resume point (`hello` deliberately carries no SSE
 * `id:`), so it reconnects into silence and goes on rendering a list the server
 * has moved past. Only a reload fixed that.
 *
 * So the client records the gap itself. A view that is safe to move — nothing
 * selected, nothing open, cursor and scroll at the top — makes it good quietly;
 * a view whose user is mid-triage announces it through the same pill, because a
 * gap may no more move rows under a cursor than an arrival may.
 */
let gap = false;
let gapVersion = 0;
const gapListeners = new Set<() => void>();

function notifyGap(): void {
  gapVersion += 1;
  for (const listener of [...gapListeners]) listener();
}

export function subscribeStreamGap(listener: () => void): () => void {
  gapListeners.add(listener);
  return () => {
    gapListeners.delete(listener);
  };
}

export function streamGapVersion(): number {
  return gapVersion;
}

/** The stream was interrupted; the cache may be missing changes it cannot name. */
export function recordStreamGap(): void {
  if (gap) return;
  gap = true;
  notifyGap();
}

/** The gap has been made good — by a refetch, or by a server-driven resync. */
export function clearStreamGap(): void {
  if (!gap) return;
  gap = false;
  notifyGap();
}

export function hasStreamGap(): boolean {
  return gap;
}

/** Test seam: forget the gap between cases. */
export function resetStreamGap(): void {
  gap = false;
  notifyGap();
}
